import * as _ from 'lodash-es';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
} from 'react';
import { useEffectEvent } from 'use-effect-event';
import { useRightPanelPinnedWidth } from '../app/layout/RightPanel';
import { useSidebarOpenState } from '../app/layout/SidebarOpenStateContext';
import { LAYOUT_DEBOUNCE_TIMEOUT } from '../app/layout/SidebarShiftStateProvider';
import useGetLayoutBaseWidth from '../app/layout/useGetLayoutBaseWidth';
import useAppSelector from '../app/redux/useAppSelector';
import PostLayoutSizeProvider, {
  getCurrentPostLayoutSize,
} from '../utils/responsive/PostLayoutSizeProvider';
import { SIDEBAR_WIDTH } from './PostLayoutSizes';
import type {
  ExpandableAreas,
  PriorityList,
} from './usePostLayoutStateReducer';
import usePostLayoutStateReducer from './usePostLayoutStateReducer';

type PostLayoutState = {
  isTOCExpanded: boolean;
  canTOCExpand: boolean;
  canMagneticWallExpand: boolean;
  isMagneticWallExpanded: boolean;
};

const PostLayoutStateContext = React.createContext<PostLayoutState>({
  isTOCExpanded: false,
  canTOCExpand: false,
  canMagneticWallExpand: false,
  isMagneticWallExpanded: false,
});

const ToggleTOCContext = React.createContext<(forceValue?: boolean) => void>(
  _.noop,
);

interface PostLayoutProviderProps {
  children: React.ReactNode;
  expandableAreas: ExpandableAreas;
  initialPriorityList?: PriorityList;
}

const PostLayoutProvider = ({
  children,
  expandableAreas,
  initialPriorityList,
}: PostLayoutProviderProps) => {
  const { isSidebarOpen, toggleSidebar } = useSidebarOpenState();

  const getLayoutBaseWidth = useGetLayoutBaseWidth();

  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );

  const {
    state: { expandedAreas, lastWidth },
    dispatch: dispatchPostLayoutAction,
    canMagneticWallExpand,
    canTOCExpand,
  } = usePostLayoutStateReducer({
    isSidebarOpenInitially: isSidebarOpen,
    expandableAreas,
    initialPriorityList,
    getInitialWidth: getLayoutBaseWidth,
  });

  const isMagneticWallExpanded = expandedAreas.has('magneticWall');
  const isTOCExpanded = expandedAreas.has('toc');
  const shouldOpenSidebar = expandedAreas.has('sidebar');

  const postLayoutSize = getCurrentPostLayoutSize({
    baseWidth: shouldOpenSidebar ? lastWidth - SIDEBAR_WIDTH : lastWidth,
  });

  const onToggleSidebar = useEffectEvent(toggleSidebar);
  // Sync back sidebar state
  useEffect(() => {
    onToggleSidebar(shouldOpenSidebar);
  }, [shouldOpenSidebar]);

  const toggleTOC = useCallback(() => {
    dispatchPostLayoutAction({
      type: 'TOGGLE_AREA',
      payload: {
        area: 'toc',
        isExpanded: !isTOCExpanded,
      },
    });
  }, [isTOCExpanded, dispatchPostLayoutAction]);

  useEffect(() => {
    if (magneticWallFocus?.hasExpandIntent) {
      dispatchPostLayoutAction({
        type: 'TOGGLE_AREA',
        payload: {
          area: 'magneticWall',
          isExpanded: true,
        },
      });
    }
  }, [dispatchPostLayoutAction, magneticWallFocus?.hasExpandIntent]);

  useLayoutEffect(() => {
    dispatchPostLayoutAction({
      type: 'TOGGLE_AREA',
      payload: {
        area: 'sidebar',
        isExpanded: isSidebarOpen,
      },
    });
  }, [dispatchPostLayoutAction, isSidebarOpen]);

  const handleResize = useEffectEvent(() => {
    dispatchPostLayoutAction({
      type: 'RESIZE',
      payload: getLayoutBaseWidth(),
    });
  });

  const isRightPanelPinned = useRightPanelPinnedWidth() != null;

  // Immediately update the layout when the right panel pinned status changes
  useEffect(() => {
    handleResize();
  }, [isRightPanelPinned]);

  // When the layout base width changes, resizing will be debounced
  useEffect(() => {
    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );

    debouncedHandleResize();

    return () => {
      debouncedHandleResize.cancel();
    };
  }, [getLayoutBaseWidth]);

  // When the window is resized, resizing will be debounced
  useEffect(() => {
    const debouncedHandleResize = _.debounce(
      handleResize,
      LAYOUT_DEBOUNCE_TIMEOUT,
    );

    window.addEventListener('resize', debouncedHandleResize, { passive: true });
    return () => {
      debouncedHandleResize.cancel();
      window.removeEventListener('resize', debouncedHandleResize);
    };
  }, []);

  const postLayoutState = useMemo<PostLayoutState>(() => {
    return {
      isTOCExpanded,
      canTOCExpand,
      canMagneticWallExpand,
      isMagneticWallExpanded,
    };
  }, [
    canTOCExpand,
    isTOCExpanded,
    canMagneticWallExpand,
    isMagneticWallExpanded,
  ]);

  return (
    <ToggleTOCContext.Provider value={toggleTOC}>
      <PostLayoutStateContext.Provider value={postLayoutState}>
        <PostLayoutSizeProvider postLayoutSize={postLayoutSize}>
          {children}
        </PostLayoutSizeProvider>
      </PostLayoutStateContext.Provider>
    </ToggleTOCContext.Provider>
  );
};

export const usePostLayoutState = () => useContext(PostLayoutStateContext);
export const useToggleTOC = () => useContext(ToggleTOCContext);

export default PostLayoutProvider;
